import React, { useEffect, useState } from "react";
import {
    Modal,
    Form,
    Input,
    Select,
    Button,
    Rate,
    DatePicker,
    message,
} from "antd";
import dayjs from "dayjs"; // 使用dayjs替代moment
import "./index.less";
import { saveFeedback } from "@/util/http/Lightning/index";
import { ISaveFeedbackReqModel } from "@/util/http/Lightning/requestType";

interface FeedbackFormValues {
  feedbackType: string;
  rating: number;
  startDate: dayjs.Dayjs | null; // 日期部分
  startTime: dayjs.Dayjs | null; // 时间部分（新增）
  location: string;
  Comments: string;
}

interface FeedbackModalProps {
  visible: boolean;
  onClose: () => void;
}

const { TextArea } = Input;
const { Option } = Select;

const LightningFeedbackModal: React.FC<FeedbackModalProps> = ({
    visible,
    onClose,
}) => {
    const [form] = Form.useForm();
    const [dateTimeError, setDateTimeError] = useState("");

    // 监听日期和时间变化，实时更新错误消息
    useEffect(() => {
        // 使用form.getFieldValue直接获取字段值
        const startDate = form.getFieldValue("startDate");
        const startTime = form.getFieldValue("startTime");

        // 当两者都有值或都无值时，清除错误消息
        if (startDate && startTime) {
            setDateTimeError("");
        }
    }, [form.getFieldValue("startDate"), form.getFieldValue("startTime")]); // 关键修改：监听具体字段变化

    const handleCancel = () => {
        onClose();
        form.resetFields();
        setDateTimeError("");
    };

    const handleOk = async () => {
        setDateTimeError("");

        const { startDate, startTime } = form.getFieldsValue();

        // 日期时间校验
        if ((startDate && !startTime) || (!startDate && startTime)) {
            setDateTimeError("Please select both the date and the time.");
            return;
        }

        form
            .validateFields()
            .then(async (validatedValues: FeedbackFormValues) => {
                // 后续提交逻辑保持不变
                let combinedStartDate = "";
                if (validatedValues.startDate && validatedValues.startTime) {
                    // 日期时间组合逻辑
                    const year = validatedValues.startDate.year();
                    const month = validatedValues.startDate.month() + 1;
                    const day = validatedValues.startDate.date();
                    const hour = validatedValues.startTime.hour();
                    const minute = validatedValues.startTime.minute();

                    combinedStartDate = dayjs(
                        `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")} ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
                        "YYYY-MM-DD HH:mm"
                    ).toISOString();
                }

                // 构造API数据并提交
                const apiData: ISaveFeedbackReqModel = {
                    lightningType: validatedValues.feedbackType ?? "",
                    star: validatedValues.rating ?? 0,
                    startDate: combinedStartDate,
                    endDate: "",
                    location: validatedValues.location ?? "",
                    comments: validatedValues.Comments ?? "",
                };

                try {
                    const res = await saveFeedback(apiData);
                    if (res.status === 200) {
                        message.success("Feedback saved successfully!");
                        onClose();
                        form.resetFields();
                    } else {
                        message.error("Failed to save feedback");
                    }
                } catch (error) {
                    console.error("Error saving feedback:", error);
                    message.error("An error occurred while saving feedback");
                }
            })
            .catch((errorInfo) => {
                console.log("Validation failed:", errorInfo);
            });
    };

    const onRatingChange = (value: number) => {
        console.log("Rating changed to:", value);
    };

    return (
        <Modal
            className="feedback-modal"
            title="Lightning Feedback"
            visible={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            okText="Save"
            cancelText="Cancel"
            width={350}
            footer={[
                <Button key="cancel" onClick={handleCancel} className="feedback-cancel-btn">
                  Cancel
                </Button>,
                <Button key="save" onClick={handleOk} className="feedback-save-btn">
                  Save
                </Button>,
            ]}
            getContainer={document.getElementById("lightning-component")}
        >
            <Form
                form={form}
                name="lightningFeedbackForm"
                layout="vertical"
                initialValues={{
                    feedbackType: "FirstStrike",
                    rating: 0,
                }}
                scrollToFirstError
            >
                {/* 表单其他部分保持不变 */}
                <Form.Item name="feedbackType">
                    <Select placeholder="Select a feedback type">
                        <Option value="FirstStrike">First Strike</Option>
                        <Option value="Detected">Detected Strike</Option>
                        <Option value="Probability">Probability</Option>
                        <Option value="AllClear">Risk Zone</Option>
                    </Select>
                </Form.Item>

                <Form.Item name="rating">
                    <Rate
                        onChange={onRatingChange}
                        allowHalf={false}
                        tooltips={["terrible", "bad", "normal", "good", "wonderful"]}
                        className="feedback-rate"
                    />
                </Form.Item>

                <div className="feedback-date-content">
                    <span className="date-picker-divider">Date</span>
                    <div className="feedback-date-div">
                        <Form.Item name="startDate">
                            <DatePicker className="feedback-date" placeholder="Select Date" />
                        </Form.Item>
                        <span className="date-picker-divider">|</span>
                        <Form.Item name="startTime">
                            <DatePicker
                                className="feedback-date"
                                picker="time"
                                format="HH:mm"
                                placeholder="Select Time"
                                inputReadOnly
                            />
                        </Form.Item>
                    </div>
                    {dateTimeError && (
                        <div className="feedback-date-error">
                            {dateTimeError}
                        </div>
                    )}
                </div>

                <Form.Item name="location" label="Location">
                    <Input placeholder="Enter Location" />
                </Form.Item>

                <Form.Item name="Comments" label="Add Your Comments">
                    <TextArea rows={4} placeholder="Please describe your feedback in detail" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default LightningFeedbackModal;
